import React, { useCallback, useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  ContentTemplate,
  DateOnlySemanticDisplayFormat,
  Progress,
  PAYLOAD_DATE_FORMAT
} from 'web-library';
import { UseFormReturn } from 'react-hook-form';
import { CandidateProfileResponseObject } from 'web-library/models/AccountManagementConfiguration';
import ToggleDisplayEditFormItem from '../../../components/ToggleDisplayEditFormItem';
import { fallbackDashString } from '../../../utils/StringUtils';
import { useGetIExamMasterCode } from '../../../api-refactored/user/useGetIExamMasterCode';
import ControlledAutocomplete from '../../../components/form-items/ControlledAutocomplete';
import {
  getIExamDropdownItemLabel,
  getIExamMasterCodeSelected,
} from '../../../modules/accountManagement/logic/useCreateCandidateForm';
import { convertPrStatusToDisplayName } from '../../../modules/accountManagement/logic/usePrCitizenshipForm';
import { useStyles } from './CandidatePersonalParticulars';
import { useCandidateNationalityDetails } from './useCreateEditViewCandidateAccountDetails';
import { CreateEditCandidateSchemaData } from './CreateEditCandidateSchema';
import { usePrCitizenshipForm } from './usePrCitizenshipForm';
import { IExamMasterCodeItem } from '../../../api/user/model';
import dayjs from 'dayjs';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

/**
 * All the field in this component is disabled by default in edit state.
 * No requirement to allow the editing at the moment.
 *
 * @param examCandidateDetail candidate particular info
 * @param isEditState is editing state
 */

const NATIONALITY = (t: TFunction) => t('exam->>ReviewExam.Nationality');
const NATIONALITY_ISSUE_DATE = (t: TFunction) => t('exam->>ReviewExam.NationalityIssueDate');

const defaultDOBPickerProps = {
  placeholder: 'Enter date of birth',
  disableFuture: true,
  format: DateOnlySemanticDisplayFormat,
};

const NationalityDetails = ({
  candidateProfileDetails,
  isEditState = false,
  form,
  prStatus,
  setPrStatus,
  citizenshipType,
  setCitizenshipType,
}: {
  candidateProfileDetails?: CandidateProfileResponseObject;
  isEditState?: boolean;
  form?: UseFormReturn<CreateEditCandidateSchemaData>;
  prStatus: IExamMasterCodeItem | undefined | {};
  setPrStatus: React.Dispatch<React.SetStateAction<IExamMasterCodeItem | undefined | {}>>;
  citizenshipType: IExamMasterCodeItem | undefined | {};
  setCitizenshipType: React.Dispatch<React.SetStateAction<IExamMasterCodeItem | undefined | {}>>;
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { data: iExamsData } = useGetIExamMasterCode();

  const {
    defaultNationalityIssueDate,
    citizenshipDefaultObject,
    nationalityDefaultObject,
    prStatusDefaultObject,
    isNationalityDetailsValueUndefined,
  } = useCandidateNationalityDetails({
    iExamsData,
    candidateProfileDetails,
  });

  const [nationalityIssueDateDisplay, setNationalityIssueDateDisplay] = useState<string>(
    defaultNationalityIssueDate || ''
  );

  const { renamedPrStatusWithSorting } = usePrCitizenshipForm({
    iExamsData,
  });

  const selectedIdType = form?.watch('IdType');

  const isFin =
    form?.watch('NricFin')?.[0] === 'F' ||
    form?.watch('NricFin')?.[0] === 'G' ||
    form?.watch('NricFin')?.[0] === 'M';

  const onChangePrStatus = useCallback(
    (_e: any, prValue: any) => {
      form?.setValue('prStatus', prValue);
      form?.trigger('prStatus');
      setPrStatus(prValue);
      if (prValue?.codeValue === 'S' && !isFin) {
        const iExamsDataSCCitizenshipCodeValue = iExamsData?.citizenship.find(
          (data) => data.codeValue === 'SC'
        );
        form?.setValue('citizenship', iExamsDataSCCitizenshipCodeValue);
        setCitizenshipType(iExamsDataSCCitizenshipCodeValue);
      }
      if (prValue?.codeValue === 'P' || isFin) {
        const iExamsDataPrCitizenshipCodeValue = iExamsData?.citizenship.find(
          (data) => data.codeValue === 'PR'
        );
        form?.setValue('citizenship', iExamsDataPrCitizenshipCodeValue);
        setCitizenshipType(iExamsDataPrCitizenshipCodeValue);
      }
    },
    [form, iExamsData?.citizenship, isFin, setCitizenshipType, setPrStatus]
  );

  useEffect(() => {
    form?.reset({
      ...form.getValues(),
      citizenship: citizenshipDefaultObject,
      prStatus: prStatusDefaultObject,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prStatusDefaultObject, citizenshipDefaultObject]);

  if (isNationalityDetailsValueUndefined || renamedPrStatusWithSorting === undefined) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={NATIONALITY(t)}
        valueText={candidateProfileDetails?.nationality}
        containerClassName={classes.formElementContainer}
        EditComponent={
          form && (
            <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
              label={NATIONALITY(t)}
              placeholder="Select nationality"
              id="input-select-nationality"
              control={form.control}
              name="nationality"
              error={!!form.formState.errors?.nationality}
              helperText={form.formState.errors?.nationality?.message?.toString()}
              options={iExamsData ? iExamsData.nationality : []}
              defaultValue={nationalityDefaultObject}
              getOptionLabel={getIExamDropdownItemLabel}
              isOptionEqualToValue={getIExamMasterCodeSelected}
              isRequired
            />
          )
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        allHide
        labelText={NATIONALITY_ISSUE_DATE(t)}
        valueText={dayjs(defaultNationalityIssueDate,PAYLOAD_DATE_FORMAT).format(DateOnlySemanticDisplayFormat)}
        containerClassName={classes.halfWidthRightItem}
        EditComponent={
            form &&
          <ControlledDatePicker
            label={NATIONALITY_ISSUE_DATE(t)}
            onChange={(date) => {
              setNationalityIssueDateDisplay(date || '');
              form?.setValue('nationalityIssueDate', date || '');
            }}
            control={form?.control}
            name="nationalityIssueDate"
            inputVariant="filled"
            defaultValue={nationalityIssueDateDisplay}
            {...defaultDOBPickerProps}
          />
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('exam->>ReviewExam.PrStatus')}
        valueText={convertPrStatusToDisplayName(
          fallbackDashString(candidateProfileDetails?.prStatus)
        )}
        containerClassName={!isEditState ? classes.halfWidthLeftItem : classes.formElementContainer}
        EditComponent={
          form && (
            <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
              control={form.control}
              label={t('exam->>ReviewExam.PrStatus')}
              placeholder="Select pr status"
              name="prStatus"
              id="input-select-prStatus"
              error={!!form.formState.errors?.prStatus}
              helperText={form.formState.errors?.prStatus?.message?.toString()}
              fullWidth
              disabled={!!prStatusDefaultObject || selectedIdType === 'P' || isFin}
              onChange={onChangePrStatus}
              options={renamedPrStatusWithSorting as IExamMasterCodeItem[]}
              getOptionLabel={(option) => option.codeValueDescription === "ALIEN" ? "FOREIGNER" : option.codeValueDescription}
              isOptionEqualToValue={getIExamMasterCodeSelected}
              isRequired
              readOnly
              defaultValue={null}
            />
          )
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('exam->>ReviewExam.CitizenshipType')}
        valueText={candidateProfileDetails?.citizenship}
        containerClassName={classes.formElementContainer}
        EditComponent={
          form && (
            <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
              control={form.control}
              label={t('exam->>ReviewExam.CitizenshipType')}
              name="citizenship"
              placeholder="Select citizenship type"
              id="input-select-citizenship"
              disabled
              getOptionLabel={getIExamDropdownItemLabel}
              isOptionEqualToValue={getIExamMasterCodeSelected}
              options={iExamsData ? iExamsData.citizenship : []}
              isRequired
              defaultValue={null}
            />
          )
        }
      />
    </>
  );
};
export default NationalityDetails;
